import { Link } from 'react-router-dom';
import { useScrollReveal } from '../../hooks/useScrollReveal';

export interface Project {
  image: string;
  imageAlt: string;
  name: string;
  location: string;
  type: string;
  metric?: string;
  metricLabel?: string;
}

export interface TestimonialsSectionProps {
  eyebrow: string;
  title: string;
  projects: Project[];
  ctaText: string;
  ctaLink: string;
}

export default function TestimonialsSection({
  eyebrow,
  title,
  projects,
  ctaText,
  ctaLink,
}: TestimonialsSectionProps) {
  const { ref, visible } = useScrollReveal<HTMLDivElement>();

  const featured = projects[0];
  const secondary = projects.slice(1, 3);
  const fullWidth = projects[3];

  return (
    <section className="bg-charcoal-mid py-28 lg:py-40">
      <div className="mx-auto px-6 lg:px-12 xl:px-20">
        {/* Heading row */}
        <div
          ref={ref}
          className={`flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8 mb-10 transition-all duration-700 ease-out ${
            visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <div>
            <div className="flex items-center gap-4 mb-5">
              <span className="block w-8 h-px bg-gold flex-shrink-0" />
              <span className="text-gold text-[9px] font-semibold tracking-[0.38em] uppercase">
                {eyebrow}
              </span>
            </div>
            <h2
              className="font-serif text-white leading-tight"
              style={{ fontSize: 'clamp(2rem, 3.8vw, 3.2rem)', fontWeight: 300 }}
            >
              {title}
            </h2>
          </div>
          <Link
            to={ctaLink}
            className="whitespace-nowrap inline-flex items-center gap-2 text-white/40 text-[9px] font-bold tracking-[0.28em] uppercase hover:text-gold transition-colors duration-300 cursor-pointer group flex-shrink-0"
          >
            {ctaText}
            <i className="ri-arrow-right-line transition-transform duration-300 group-hover:translate-x-1"></i>
          </Link>
        </div>

        {/* Outcome strip */}
        <div
          className={`grid grid-cols-3 border border-white/8 mb-6 transition-all duration-700 ease-out ${
            visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
          }`}
        >
          {[
            { icon: 'ri-flashlight-flash-line', stat: '70%+', label: 'Average Energy Reduction' },
            { icon: 'ri-building-4-line', stat: '10+', label: 'Commercial Application Types' },
            { icon: 'ri-map-pin-2-fill', stat: 'FL', label: 'Tampa Bay & Nationwide' },
          ].map((item, i) => (
            <div
              key={item.label}
              className={`flex items-center gap-4 px-6 lg:px-8 py-5 ${
                i < 2 ? 'border-r border-white/8' : ''
              }`}
            >
              <div className="w-8 h-8 flex-shrink-0 flex items-center justify-center">
                <i className={`${item.icon} text-gold text-lg`}></i>
              </div>
              <div>
                <p className="font-serif text-white font-light leading-none text-xl mb-0.5">
                  {item.stat}
                </p>
                <p className="text-white/30 text-[9px] tracking-wider hidden sm:block">{item.label}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Asymmetric editorial grid — row 1 */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-3 mb-3">
          {featured && (
            <FeaturedCard project={featured} index={0} className="lg:col-span-7" />
          )}
          <div className="lg:col-span-5 flex flex-col gap-3">
            {secondary.map((p, i) => (
              <FeaturedCard key={p.name} project={p} index={i + 1} className="flex-1" />
            ))}
          </div>
        </div>

        {/* Full-width bottom card */}
        {fullWidth && <FullWidthCard project={fullWidth} />}
      </div>
    </section>
  );
}

function FeaturedCard({
  project,
  index,
  className = '',
}: {
  project: Project;
  index: number;
  className?: string;
}) {
  const { ref, visible } = useScrollReveal<HTMLDivElement>();

  return (
    <div
      ref={ref}
      className={`relative overflow-hidden group cursor-default transition-all duration-700 ease-out ${
        visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
      } ${className}`}
      style={{
        transitionDelay: `${index * 110}ms`,
        minHeight: index === 0 ? '440px' : '198px',
      }}
    >
      <img
        src={project.image}
        alt={project.imageAlt}
        className="absolute inset-0 w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-700 ease-out"
      />
      {/* Layered overlays */}
      <div className="absolute inset-0 bg-gradient-to-t from-charcoal/98 via-charcoal/35 to-transparent" />
      <div className="absolute inset-0 bg-gradient-to-r from-charcoal/25 to-transparent" />

      {/* Metric badge — top right */}
      {project.metric && (
        <div className="absolute top-5 right-5 flex flex-col items-end">
          <span className="font-serif text-gold text-xl font-light leading-none">
            {project.metric}
          </span>
          {project.metricLabel && (
            <span className="text-white/45 text-[8px] tracking-[0.18em] uppercase mt-0.5">
              {project.metricLabel}
            </span>
          )}
        </div>
      )}

      {/* Content */}
      <div className="absolute inset-0 flex flex-col justify-end p-7 lg:p-9">
        <span className="inline-flex items-center gap-2 text-[9px] font-semibold tracking-[0.32em] uppercase text-gold mb-3">
          <span className="w-1 h-1 bg-gold rounded-full flex-shrink-0" />
          {project.type}
        </span>
        <h3
          className="font-serif text-white leading-tight mb-1.5"
          style={{
            fontSize:
              index === 0
                ? 'clamp(1.4rem, 2.2vw, 2rem)'
                : 'clamp(1.1rem, 1.6vw, 1.4rem)',
            fontWeight: 400,
          }}
        >
          {project.name}
        </h3>
        <p className="text-white/40 text-xs tracking-wide">{project.location}</p>
      </div>

      {/* Gold hover border */}
      <div className="absolute inset-0 border border-gold/0 group-hover:border-gold/35 transition-all duration-500 pointer-events-none" />
    </div>
  );
}

function FullWidthCard({ project }: { project: Project }) {
  const { ref, visible } = useScrollReveal<HTMLDivElement>();

  return (
    <div
      ref={ref}
      className={`relative overflow-hidden group cursor-default transition-all duration-700 ease-out ${
        visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
      }`}
      style={{ transitionDelay: '330ms', height: '230px' }}
    >
      <img
        src={project.image}
        alt={project.imageAlt}
        className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-700 ease-out"
      />
      <div className="absolute inset-0 bg-gradient-to-r from-charcoal/92 via-charcoal/55 to-transparent" />

      {/* Horizontal content */}
      <div className="absolute inset-0 flex items-center justify-between px-9 lg:px-14">
        <div>
          <span className="inline-flex items-center gap-2 text-[9px] font-semibold tracking-[0.32em] uppercase text-gold mb-3">
            <span className="w-1 h-1 bg-gold rounded-full flex-shrink-0" />
            {project.type}
          </span>
          <h3
            className="font-serif text-white leading-tight mb-1"
            style={{ fontSize: 'clamp(1.3rem, 2vw, 1.7rem)', fontWeight: 400 }}
          >
            {project.name}
          </h3>
          <p className="text-white/38 text-xs tracking-wide">{project.location}</p>
        </div>
        {/* Right metric */}
        {project.metric && (
          <div className="hidden sm:flex flex-col items-end flex-shrink-0">
            <span className="font-serif text-gold text-3xl font-light leading-none">
              {project.metric}
            </span>
            {project.metricLabel && (
              <span className="text-white/35 text-[9px] tracking-wider uppercase mt-1">
                {project.metricLabel}
              </span>
            )}
          </div>
        )}
      </div>

      <div className="absolute inset-0 border border-gold/0 group-hover:border-gold/30 transition-all duration-500 pointer-events-none" />
    </div>
  );
}
